import React, { useCallback, useState } from 'react';
import PropTypes from 'prop-types';
import { Loading } from 'components/Loading';
import { Button } from '@ontruck/orc';
import { DOCUMENT_STATUS } from 'components/CarrierDetails/Constants';
import { ReactComponent as UploadIcon } from 'assets/icons/upload.svg';
import { useDropzone } from 'react-dropzone';
import cx from 'classnames';
import { postDocumentUpload, postImageUpload, deleteImages } from './http';
import { readFile } from 'utils/file';
import { MAX_FILE_SIZE } from './const';

const ACCEPTED_PDF = ['application/pdf'];
const ACCEPTED_IMAGES = ['image/jpg', 'image/jpeg', 'image/png'];
const t = {
  uploadAgain: 'Upload Again',
  renewDocument: 'Renew Document',
  uploadDocument: 'Upload Document',
  finish: 'Finish',
  uploadNextSide: 'Upload next side',
  uploadHelp: 'Upload the next side of the document, if you need it.',
  error: 'There was an error uploading the document.',
};

const DocumentsUpload = ({ className, document, refetchDocuments, entity_uuid, entity_type }) => {
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState(null);

  const uploadFile = useCallback(
    async (file, uploadFunction) => {
      try {
        setError(null);
        setUploading(true);
        const readedFile = file ? await readFile(file) : '';
        await uploadFunction(entity_uuid, entity_type, document.document_type.label, readedFile);
        await refetchDocuments();
      } catch (err) {
        console.error('uploadFiles', err);
        setError('errorUploadingDocuments');
      } finally {
        setUploading(false);
      }
    },
    [document, refetchDocuments, entity_uuid, entity_type]
  );

  const checkIfIsMoreThanOneFile = useCallback((files) => {
    return files.length === 1;
  }, []);

  const checkIfItsPDF = useCallback((files) => {
    return files[0].type === ACCEPTED_PDF[0];
  }, []);

  const checkFileSize = useCallback(
    (files) => files.every((file) => file.size <= MAX_FILE_SIZE),
    []
  );

  const checkAndSetErrors = useCallback(
    (files) => {
      if (!checkIfIsMoreThanOneFile(files)) {
        setError('errorMoreThanOneFile');
        return false;
      } else if (!checkFileSize(files)) {
        setError('errorExceededFileSize');
        return false;
      } else {
        return true;
      }
    },
    [checkIfIsMoreThanOneFile, checkFileSize]
  );

  const onDrop = useCallback(
    (acceptedFiles) => {
      setError(null);
      if (checkAndSetErrors(acceptedFiles)) {
        uploadFile(
          acceptedFiles[0],
          checkIfItsPDF(acceptedFiles) || !document.two_sides ? postDocumentUpload : postImageUpload
        );
      }
    },
    [uploadFile, checkAndSetErrors, checkIfItsPDF, document]
  );

  const handleFinishProcess = useCallback(() => {
    uploadFile(null, postDocumentUpload);
  }, [uploadFile]);

  const handleCancelProcess = useCallback(() => {
    uploadFile(null, deleteImages);
  }, [uploadFile]);

  const showUploadNextSide = document.status === DOCUMENT_STATUS.status_pending_next_face;

  const { getRootProps, getInputProps } = useDropzone({
    onDrop,
    accept: [...ACCEPTED_IMAGES, ...(showUploadNextSide ? [] : ACCEPTED_PDF)],
  });
  const dropzoneProps = getRootProps();
  const HIDE_BUTTON_STATUS = [
    DOCUMENT_STATUS.status_uploading,
    DOCUMENT_STATUS.status_pending_next_face,
  ];
  const showUploadButton = !HIDE_BUTTON_STATUS.includes(document.status);

  return (
    <div className={className} data-testid="documents-upload">
      <div className="documents-upload" data-testid={`documents-upload-${document.entity_type}`}>
        <div
          {...getRootProps({
            className: `documents-upload__droparea`,
          })}
        >
          <input {...getInputProps()} data-testid="documents-upload__input" />
        </div>
        <div
          className={cx('documents-upload__item', {
            'documents-upload__item--next-face': showUploadNextSide,
          })}
        >
          {showUploadNextSide && (
            <div className="documents-upload__next-side" data-testid="documents-upload__next-side">
              {uploading && <Loading className="documents-upload__spinner" />}
              {!uploading && (
                <>
                  <div className="documents-upload__next-side-action">
                    <Button
                      className="documents-upload__finish documents-upload__button documents-upload__button--auto"
                      text={t.finish}
                      ghost
                      skin="primary"
                      loading={uploading}
                      disabled={uploading}
                      onClick={handleFinishProcess}
                      data-testid="documents-upload__finish-button"
                    />
                    <Button
                      className="documents-upload__upload-other documents-upload__button documents-upload__button--auto"
                      text={t.uploadNextSide}
                      onClick={dropzoneProps.onClick}
                      skin="primary"
                      loading={uploading}
                      disabled={uploading}
                      data-testid="documents-upload__next-side-button"
                    />
                    <Button
                      icon="close-thin"
                      nude
                      className="documents-upload__cancel-icon"
                      onClick={handleCancelProcess}
                      loading={uploading}
                      disabled={uploading}
                      data-testid="documents-upload__cancel-button"
                    />
                  </div>
                  <span className="documents-upload__next-side-label">{t.uploadHelp}</span>
                </>
              )}
            </div>
          )}
          {!showUploadNextSide && (
            <>
              <div className="documents-upload__tag-container"></div>
              <div className="documents-upload__actions">
                {uploading && <Loading className="documents-upload__spinner" />}
                {showUploadButton && !uploading && (
                  <Button
                    className="documents-upload__button-upload"
                    data-testid="documents-upload__button"
                    nude
                    skin="primary"
                    disabled={uploading}
                    onClick={dropzoneProps.onClick}
                  >
                    <UploadIcon />
                  </Button>
                )}
                {error && <span className="documents-upload__error">{t.error}</span>}
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};
DocumentsUpload.propTypes = {
  className: PropTypes.string,
  document: PropTypes.object.isRequired,
  refetchDocuments: PropTypes.func.isRequired,
  entity_uuid: PropTypes.string.isRequired,
  entity_type: PropTypes.string.isRequired,
};
export default DocumentsUpload;
